import { CodeEditor } from '@acrodata/code-editor';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CapturumSharedModule } from '@capturum/ui/api';
import { CapturumDropdownModule } from '@capturum/ui/dropdown';
import { languages } from '@codemirror/language-data';
import { EditorState, Extension, Prec } from '@codemirror/state';
import { environment } from '@environments/environment';
import { FormlySelectOptionsPipe } from '@features/flow-ui-creator/pipes/select-options-pipe.pipe';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormulaVariablesService } from '@shared/services/formula-variables.service';
import { LspCatalogService } from '@shared/services/lsp-catalog.service';
import { backspaceKeymap, tagHighlighter } from '@shared/utils/codemirror.util';
import { buildFormulaIntelligence } from '@shared/utils/formula-completion.util';
import { DndDropEvent, DndModule } from 'ngx-drag-drop';
import { SelectChangeEvent as DropdownChangeEvent } from 'primeng/select';

@Component({
  selector: 'app-formly-dto-key-input-type',
  imports: [
    FormsModule,
    FormlyModule,
    ReactiveFormsModule,
    TranslateModule,
    CodeEditor,
    DndModule,
    CapturumDropdownModule,
    FormlySelectOptionsPipe,
    AsyncPipe,
    CapturumSharedModule,
  ],
  templateUrl: './formly-dto-key-input-type.component.html',
  styleUrl: './formly-dto-key-input-type.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyDtoKeyInputTypeComponent extends FieldType implements OnInit {
  readonly #lspCatalog = inject(LspCatalogService);
  readonly #formulaVariables = inject(FormulaVariablesService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #changeDetector = inject(ChangeDetectorRef);
  readonly #translate = inject(TranslateService);

  public languages = languages;
  public extensions: Extension[] = [];
  public inputValueModel = new FormControl<string>('');

  public ngOnInit(): void {
    if (!this.props?.extensions) {
      this.extensions = [Prec.highest(backspaceKeymap)];

      if (!this.props.multiline) {
        this.extensions.push(
          EditorState.transactionFilter.of((tr) => {
            if (tr.changes.empty) return tr;
            if (tr.newDoc.lines > 1 && !tr.isUserEvent('input.paste')) {
              return [];
            }

            const removeNewlines = [];

            tr.changes.iterChanges((_fromA, _toA, fromB, _toB, ins) => {
              const lineIter = ins.iterLines().next();

              if (ins.lines <= 1) return;
              // skip the first line
              let len = fromB + lineIter.value.length;

              lineIter.next();
              // for the next lines, remove the leading NL
              for (; !lineIter.done; lineIter.next()) {
                removeNewlines.push({ from: len, to: len + 1 });
                len += lineIter.value.length + 1;
              }
            });

            return [tr, { changes: removeNewlines, sequential: true }];
          }),
        );
      }

      if (!this.props.noPlaceholder) {
        this.extensions.push(tagHighlighter);

        if (environment.flow.lspEnabled) {
          this.loadFormulaIntelligence();
        }
      }
    }

    if (this.formControl.value) {
      this.inputValueModel.setValue(this.formControl.value);
    }
  }

  private loadFormulaIntelligence(): void {
    this.#lspCatalog
      .getFormulas()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((catalog) => {
        const getVariables = (): string[] =>
          (this.props?.['variables'] as string[] | undefined) ?? this.#formulaVariables.getVariables();

        this.extensions = [
          ...this.extensions,
          ...buildFormulaIntelligence(catalog, getVariables, (key, params) => this.#translate.instant(key, params)),
        ];
        this.#changeDetector.markForCheck();
      });
  }

  public onDrop(event: DndDropEvent): void {
    if (event.data) {
      const formValue = this.formControl?.value || '';
      const data = this.props.noPlaceholder ? event.data : `{{${event.data}}}`;

      this.formControl.patchValue(`${formValue}${data}`, {
        emitEvent: false,
      });
    }
  }

  public onOptionChange(event: DropdownChangeEvent): void {
    this.formControl.patchValue(event.value, {
      emitEvent: false,
    });
  }
}
